import * as React from 'react'
import { Bell, Plus, Send, Trash2 } from 'lucide-react'
import {
  useChannels, useCreateChannel, useDeleteChannel, useDeliveries,
  useMarkRead, useTestChannel,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { formatDateTime } from '@/lib/utils'

export default function NotificationsPage() {
  const channels = useChannels()
  const [showCreate, setShowCreate] = React.useState(false)

  return (
    <>
      <PageHeader
        title="通知"
        description="渠道需通过测试投递才会启用；密钥仅保存密文，界面只显示后四位。"
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> 新建渠道
          </Button>
        }
      />

      <Tabs defaultValue="inbox">
        <TabsList>
          <TabsTrigger value="inbox">站内通知</TabsTrigger>
          <TabsTrigger value="channels">渠道</TabsTrigger>
          <TabsTrigger value="deliveries">投递记录</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox"><Inbox /></TabsContent>
        <TabsContent value="channels"><Channels query={channels} /></TabsContent>
        <TabsContent value="deliveries"><Deliveries /></TabsContent>
      </Tabs>

      <CreateChannelDialog open={showCreate} onClose={() => setShowCreate(false)} />
    </>
  )
}

function Inbox() {
  const { data, isLoading, error, refetch } = useDeliveries({ channel_kind: 'inapp', limit: 50 })
  const markRead = useMarkRead()
  const { user } = useAuth()

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={5} />
  if ((data?.items.length ?? 0) === 0) {
    return <EmptyState icon={Bell} title="暂无站内通知" description="关注的域名发生关键状态变化时会在此提醒。" />
  }

  return (
    <div className="space-y-2">
      {data!.items.map((d) => (
        <article
          key={d.id}
          className={`rounded-lg border p-3 ${d.read_at ? 'bg-card' : 'border-primary/40 bg-primary/5'}`}
        >
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium">{d.subject}</h3>
              <pre className="mt-1 whitespace-pre-wrap font-sans text-xs text-muted-foreground">{d.body}</pre>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="text-xs text-muted-foreground">
                {formatDateTime(d.sent_at ?? d.created_at, user?.timezone)}
              </span>
              {d.delay_seconds > 0 && (
                <Badge variant="muted" className="text-[10px]">
                  延迟 {Math.round(d.delay_seconds / 60)} 分钟
                </Badge>
              )}
              {!d.read_at && (
                <Button variant="ghost" size="sm" onClick={() => void markRead.mutateAsync(d.id)}>
                  标记已读
                </Button>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  )
}

function Channels({ query }: { query: ReturnType<typeof useChannels> }) {
  const { data, isLoading, error, refetch } = query
  const test = useTestChannel()
  const remove = useDeleteChannel()
  const toast = useToast()

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={3} />
  if ((data?.items.length ?? 0) === 0) {
    return <EmptyState title="尚未配置外部渠道" description="站内通知始终可用；添加邮箱、Webhook 或 Telegram 可获得实时提醒。" />
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {data!.items.map((c) => (
        <Card key={c.id}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground">{kindLabel(c.kind)}</p>
              </div>
              <Badge variant={c.status === 'active' ? 'success' : c.status === 'failed' ? 'danger' : 'muted'}>
                {channelStatusLabel(c.status)}
              </Badge>
            </div>

            <dl className="mt-3 space-y-1 text-xs text-muted-foreground">
              {Object.entries(c.config ?? {}).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="shrink-0">{k}</dt>
                  <dd className="truncate font-mono">{String(v)}</dd>
                </div>
              ))}
              {c.secret_masked && (
                <div className="flex gap-2">
                  <dt className="shrink-0">密钥</dt>
                  <dd className="font-mono">{c.secret_masked}</dd>
                </div>
              )}
            </dl>

            {c.last_error && (
              <p role="alert" className="mt-2 rounded bg-destructive/10 p-2 text-xs text-destructive">
                {c.last_error}
              </p>
            )}

            <div className="mt-3 flex gap-2">
              <Button
                variant="outline" size="sm" disabled={test.isPending}
                onClick={async () => {
                  const res = await test.mutateAsync(c.id)
                  if (res.verified) toast.success('测试投递成功', '渠道已启用')
                  else toast.error('测试投递失败', res.error)
                }}
              >
                <Send className="h-3.5 w-3.5" aria-hidden /> 发送测试
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={async () => {
                  try {
                    await remove.mutateAsync(c.id)
                    toast.success('渠道已删除')
                  } catch (err) {
                    toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden /> 删除
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function Deliveries() {
  const [status, setStatus] = React.useState('')
  const { data, isLoading, error, refetch } = useDeliveries({ status: status || undefined, limit: 100 })
  const { user } = useAuth()

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />

  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1">
        {[
          { value: '', label: '全部' },
          { value: 'sent', label: '已发送' },
          { value: 'delayed', label: '延迟中' },
          { value: 'queued', label: '排队中' },
          { value: 'failed', label: '失败' },
          { value: 'suppressed', label: '已抑制' },
        ].map((o) => (
          <Button key={o.value} variant={status === o.value ? 'default' : 'outline'} size="sm" onClick={() => setStatus(o.value)}>
            {o.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState rows={6} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState title="暂无投递记录" />
      ) : (
        <div className="rounded-lg border bg-card scroll-x">
          <table className="w-full min-w-[760px] text-sm">
            <caption className="sr-only">通知投递记录</caption>
            <thead className="border-b bg-muted/40">
              <tr>
                {['状态', '渠道', '事件', '主题', '计划发送', '尝试', '错误'].map((h) => (
                  <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data!.items.map((d) => (
                <tr key={d.id} className="border-b last:border-0">
                  <td className="px-3 py-2">
                    <Badge variant={deliveryVariant(d.status)}>{deliveryLabel(d.status)}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">{kindLabel(d.channel_kind)}</td>
                  <td className="px-3 py-2 text-xs">{d.event_type}</td>
                  <td className="px-3 py-2 max-w-xs truncate text-xs">{d.subject}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {formatDateTime(d.scheduled_at, user?.timezone)}
                    {d.delay_seconds > 0 && <span className="ml-1">(+{Math.round(d.delay_seconds / 60)}m)</span>}
                  </td>
                  <td className="px-3 py-2 text-xs tabular">{d.attempts}</td>
                  <td className="px-3 py-2 max-w-xs truncate text-xs text-destructive">{d.last_error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

function CreateChannelDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateChannel()
  const toast = useToast()
  const [kind, setKind] = React.useState('email')
  const [name, setName] = React.useState('')
  const [address, setAddress] = React.useState('')
  const [url, setUrl] = React.useState('')
  const [chatId, setChatId] = React.useState('')
  const [secret, setSecret] = React.useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const config: Record<string, unknown> =
      kind === 'email' ? { address } : kind === 'webhook' ? { url } : kind === 'telegram' ? { chat_id: chatId } : {}
    try {
      const res = await create.mutateAsync({ name, kind, config, secret: secret || undefined })
      toast.success('渠道已创建', res.notice)
      onClose()
      setName(''); setAddress(''); setUrl(''); setChatId(''); setSecret('')
    } catch (err) {
      toast.error('创建失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>新建通知渠道</DialogTitle>
          <DialogDescription>
            创建后处于未验证状态，需通过测试投递才会启用。Webhook 目标会做 SSRF 校验。
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ch-kind">渠道类型</Label>
            <select
              id="ch-kind" value={kind} onChange={(e) => setKind(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="email">邮件</option>
              <option value="webhook">Webhook</option>
              <option value="telegram">Telegram</option>
              <option value="inapp">站内</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ch-name">名称</Label>
            <Input id="ch-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          {kind === 'email' && (
            <div className="space-y-1.5">
              <Label htmlFor="ch-address">收件邮箱</Label>
              <Input id="ch-address" type="email" value={address} onChange={(e) => setAddress(e.target.value)} required />
            </div>
          )}
          {kind === 'webhook' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ch-url">Webhook URL</Label>
                <Input id="ch-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" required />
                <p className="text-[11px] text-muted-foreground">必须是 https，且不得指向内网或云元数据地址。</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ch-secret">签名密钥（可选）</Label>
                <Input id="ch-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} />
                <p className="text-[11px] text-muted-foreground">
                  用于 HMAC 签名（timestamp + body），密钥仅保存密文。
                </p>
              </div>
            </>
          )}
          {kind === 'telegram' && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ch-chat">Chat ID</Label>
                <Input id="ch-chat" value={chatId} onChange={(e) => setChatId(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ch-token">Bot Token</Label>
                <Input id="ch-token" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} required />
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>取消</Button>
            <Button type="submit" disabled={create.isPending}>创建</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function kindLabel(kind: string): string {
  return { inapp: '站内', email: '邮件', webhook: 'Webhook', telegram: 'Telegram' }[kind] ?? kind
}
function channelStatusLabel(s: string): string {
  return { unverified: '未验证', active: '已启用', failed: '失败', disabled: '已停用' }[s] ?? s
}
function deliveryLabel(s: string): string {
  return { queued: '排队中', delayed: '延迟中', sent: '已发送', failed: '失败', suppressed: '已抑制', read: '已读' }[s] ?? s
}
function deliveryVariant(s: string) {
  return ({ sent: 'success', failed: 'danger', delayed: 'warning', suppressed: 'muted', queued: 'muted' } as const)[s] ?? 'muted'
}
